import {MOON_900, TEAL_300} from '@wandb/weave/common/css/color.styles';
import {Button} from '@wandb/weave/components/Button';
import {Checkbox} from '@wandb/weave/components/Checkbox/Checkbox';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu/DropdownMenu';
import {TextField} from '@wandb/weave/components/Form/TextField';
import {Icon, IconName} from '@wandb/weave/components/Icon';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import {clsx} from 'clsx';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {AutoSizer, List} from 'react-virtualized';

import {
  getCostFromCostData,
  getTokensFromUsage,
  TraceStat,
} from '../../../pages/CallPage/cost';
import {StatusChip} from '../../../pages/common/StatusChip';
import {TraceCallSchema} from '../../../pages/wfReactInterface/traceServerClientTypes';
import {traceCallStatusCode} from '../../../pages/wfReactInterface/tsDataModelHooks';
import TraceScrubber, {ScrubberOption} from '../TraceScrubber';
import {TraceTreeFlat, TraceViewProps} from './types';
import {formatDuration, getCallDisplayName} from './utils';

interface MetricVisibilitySettings {
  showCost: boolean;
  showTokens: boolean;
  showLatency: boolean;
}

const STORAGE_KEY = 'trace-tree-metrics-visibility';

const DEFAULT_METRIC_VISIBILITY: MetricVisibilitySettings = {
  showCost: true,
  showTokens: false,
  showLatency: true,
};

const getDefaultMetricVisibility = (): MetricVisibilitySettings => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    // Failed to load from localStorage, use defaults
  }
  return DEFAULT_METRIC_VISIBILITY;
};

const saveMetricVisibility = (settings: MetricVisibilitySettings) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (e) {
    // Failed to save to localStorage, continue silently
  }
};

interface FlattenedNode {
  id: string;
  call: TraceCallSchema;
  level: number;
  isExpanded: boolean;
  isVisible: boolean;
  childrenIds: string[];
  hasDescendantErrors: boolean;
}

interface TreeNodeProps {
  node: FlattenedNode;
  style: React.CSSProperties;
  focusedCallId?: string;
  setFocusedCallId: (id: string) => void;
  rootCallId?: string;
  setRootCallId: (id: string) => void;
  onToggleExpand: (id: string, expandAll?: boolean) => void;
  deemphasizeCallIds?: string[];
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
  strictSearch?: boolean;
  onToggleStrictSearch?: () => void;
  metricVisibility: MetricVisibilitySettings;
}

const TreeNode: React.FC<TreeNodeProps> = ({
  node,
  style,
  focusedCallId,
  setFocusedCallId,
  setRootCallId,
  onToggleExpand,
  deemphasizeCallIds,
  searchQuery,
  onSearchChange,
  strictSearch,
  onToggleStrictSearch,
  metricVisibility,
}) => {
  const {id, call, level, isExpanded, childrenIds, hasDescendantErrors} = node;
  const duration = call.ended_at
    ? Date.parse(call.ended_at) - Date.parse(call.started_at)
    : null;

  const {cost, costToolTipContent} = getCostFromCostData(
    call.summary?.weave?.costs
  );
  const {tokens, tokenToolTipContent} = getTokensFromUsage(call.summary?.usage);

  const displayName = getCallDisplayName(call);
  const renderHighlightedText = () => {
    if (!searchQuery) {
      return displayName;
    }
    const searchLower = searchQuery.toLowerCase();
    const textLower = displayName.toLowerCase();

    // Note: could highlight multiple matches for better UX
    const index = textLower.indexOf(searchLower);

    if (index === -1) {
      return displayName;
    }

    return (
      <>
        {displayName.slice(0, index)}
        <span
          className={clsx(
            'font-semibold',
            'text-teal-600',
            'dark:text-teal-500'
          )}>
          {displayName.slice(index, index + searchQuery.length)}
        </span>
        {displayName.slice(index + searchQuery.length)}
      </>
    );
  };

  const chevronIcon: IconName = isExpanded ? 'chevron-down' : 'chevron-next';
  const isDeemphasized = deemphasizeCallIds?.includes(id);
  const hasChildren = childrenIds.length > 0;
  const statusCode = traceCallStatusCode(call, hasDescendantErrors);
  const indentMultiplier = 14;

  return (
    <div style={style}>
      <div
        style={{
          opacity: isDeemphasized ? 0.7 : 1,
          backgroundColor: id === focusedCallId ? `${TEAL_300}52` : undefined,
          color: id === focusedCallId ? MOON_900 : undefined,
        }}
        onClick={() => setFocusedCallId(id)}
        onDoubleClick={() => setRootCallId(id)}
        className="group h-[32px] w-full cursor-pointer select-none justify-start rounded-none px-8 text-left text-sm font-semibold tracking-normal text-moon-600 hover:bg-oblivion/[0.07] hover:text-moon-800 [&_svg]:h-18 [&_svg]:w-18">
        <div className="relative flex w-full items-center justify-between gap-8">
          <div className="flex min-w-0 flex-1 items-center">
            <div
              style={{marginLeft: level * indentMultiplier}}
              className={`h-[32px]`}
            />
            {/* Render vertical lines for each level of hierarchy */}
            {Array.from({length: level}).map((_, idx) => (
              <div
                key={`line-${idx}`}
                style={{left: idx * indentMultiplier, marginLeft: 8}}
                className="absolute top-0 h-full w-px border-l border-moon-200"
              />
            ))}
            {hasChildren ? (
              <Tooltip
                trigger={
                  <Icon
                    name={chevronIcon}
                    size="small"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      onToggleExpand(id, e.shiftKey);
                    }}
                    className="p-0.5 shrink-0 cursor-pointer rounded hover:bg-moon-300"
                  />
                }
                content={
                  isExpanded
                    ? 'Collapse'
                    : 'Expand (Shift+click to expand all children)'
                }
              />
            ) : (
              <div className="w-[18px]" />
            )}
            <div className="truncate pl-4 font-medium">
              <Tooltip
                trigger={
                  <div className="truncate">{renderHighlightedText()}</div>
                }
                content={<span>{displayName}</span>}
              />
            </div>
          </div>

          <div className="ml-8 flex shrink-0 items-center gap-4 text-xs text-moon-400">
            <div className="flex items-center gap-2 text-right">
              {metricVisibility.showCost && cost !== undefined && (
                <TraceStat
                  label={cost}
                  tooltip={costToolTipContent}
                  className="text-xs text-moon-400"
                />
              )}
              {metricVisibility.showTokens && tokens !== undefined && (
                <TraceStat
                  label={tokens}
                  tooltip={tokenToolTipContent}
                  className="text-xs text-moon-400"
                />
              )}
              {metricVisibility.showLatency && duration !== null && (
                <span className="min-w-[32px] text-moon-400">
                  {formatDuration(duration)}
                </span>
              )}
            </div>
            <div
              className={onSearchChange ? 'group-hover:hidden w-24' : 'w-24'}>
              <StatusChip value={statusCode} iconOnly />
            </div>
            {onSearchChange && (
              <div className="hidden group-hover:block w-24">
                <Button
                  variant="ghost"
                  size="small"
                  icon="filter-alt"
                  tooltip="Only show calls with this name"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onSearchChange(displayName);
                    if (onToggleStrictSearch && !strictSearch) {
                      onToggleStrictSearch();
                    }
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface TreeViewHeaderProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  strictSearch: boolean;
  onToggleStrictSearch: () => void;
  metricVisibility: MetricVisibilitySettings;
  onMetricVisibilityChange: (settings: MetricVisibilitySettings) => void;
}

const TreeViewHeader: React.FC<TreeViewHeaderProps> = ({
  searchQuery,
  onSearchChange,
  strictSearch,
  onToggleStrictSearch,
  metricVisibility,
  onMetricVisibilityChange,
}) => {
  const [traceDrawerViewOpen, setTraceDrawerViewOpen] = useState(false);
  const handleToggleMetric = (
    metric: keyof MetricVisibilitySettings,
    checked: boolean
  ) => {
    const newSettings = {...metricVisibility, [metric]: checked};
    onMetricVisibilityChange(newSettings);
  };

  return (
    <div className="flex items-center gap-8 p-8 text-sm">
      <TextField
        value={searchQuery}
        onChange={onSearchChange}
        placeholder="Filter by op name..."
        icon="filter-alt"
        extraActions={
          searchQuery !== '' && (
            <>
              <Button
                variant="ghost"
                size="small"
                onClick={onToggleStrictSearch}
                tooltip={
                  strictSearch
                    ? '(Strict) Only show matching nodes'
                    : '(Loose) Show children of matching nodes'
                }
                className={strictSearch ? 'mr-2' : 'mr-2 text-moon-500'}
                active={strictSearch}>
                Strict
              </Button>
              <Button
                variant="ghost"
                size="small"
                icon="close"
                onClick={() => onSearchChange('')}
                className="mr-6"
              />
            </>
          )
        }
      />
      <DropdownMenu.Root
        open={traceDrawerViewOpen}
        onOpenChange={setTraceDrawerViewOpen}>
        <DropdownMenu.Trigger asChild>
          <Button
            variant="secondary"
            icon="column"
            tooltip="Metric visibility settings"
            aria-label="Configure metrics visibility"
          />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content align="end" className="z-50">
            <DropdownMenu.Label className="pb-4 py-4 px-12 font-semibold">
              Metrics
            </DropdownMenu.Label>
            <DropdownMenu.Item
              className="py-2 px-8 gap-2"
              onSelect={e => {
                e.preventDefault();
                handleToggleMetric('showCost', !metricVisibility.showCost);
              }}>
              <Checkbox
                checked={metricVisibility.showCost}
                onCheckedChange={checked =>
                  handleToggleMetric('showCost', checked === true)
                }
                size="small"
              />
              <span className="ml-8">Cost</span>
            </DropdownMenu.Item>
            <DropdownMenu.Item
              className="py-2 px-8 gap-2"
              onSelect={e => {
                e.preventDefault();
                handleToggleMetric('showTokens', !metricVisibility.showTokens);
              }}>
              <Checkbox
                checked={metricVisibility.showTokens}
                onCheckedChange={checked =>
                  handleToggleMetric('showTokens', checked === true)
                }
                size="small"
              />
              <span className="ml-8">Tokens</span>
            </DropdownMenu.Item>
            <DropdownMenu.Item
              className="py-2 px-8 gap-2"
              onSelect={e => {
                e.preventDefault();
                handleToggleMetric(
                  'showLatency',
                  !metricVisibility.showLatency
                );
              }}>
              <Checkbox
                checked={metricVisibility.showLatency}
                onCheckedChange={checked =>
                  handleToggleMetric('showLatency', checked === true)
                }
                size="small"
              />
              <span className="ml-8">Latency</span>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
};

export const FilterableTreeView: React.FC<TraceViewProps> = props => {
  const {
    traceTreeFlat: propsTraceTreeFlat,
    focusedCallId,
    setFocusedCallId,
    traceSearchQuery: searchQuery,
    setTraceSearchQuery: setSearchQuery,
    traceSearchStrict: strictSearch,
    setTraceSearchStrict: setStrictSearch,
  } = props;

  const [metricVisibility, setMetricVisibility] =
    useState<MetricVisibilitySettings>(getDefaultMetricVisibility);

  const handleMetricVisibilityChange = useCallback(
    (settings: MetricVisibilitySettings) => {
      setMetricVisibility(settings);
      saveMetricVisibility(settings);
    },
    []
  );

  const [matchedCallIds, filteredCallIds, deemphasizeCallIds] = useMemo(() => {
    // First find direct matches
    const directMatches = Object.entries(props.traceTreeFlat)
      .filter(([_, node]) => {
        return (
          searchQuery === '' ||
          getCallDisplayName(node.call)
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        );
      })
      .map(([id]) => id);

    const foundCallIds = new Set<string>(directMatches);

    // In strict search mode, only show direct matches
    if (strictSearch && searchQuery !== '') {
      return [
        directMatches,
        directMatches,
        [], // No deemphasized nodes in strict mode
      ];
    }

    const filteredCallIdsSet = new Set(directMatches);

    // Process queue for both upward (parents) and downward (children)
    const itemsToProcess = [...directMatches];

    while (itemsToProcess.length > 0) {
      const id = itemsToProcess.shift();
      if (id) {
        const node = props.traceTreeFlat[id];

        // Add parents
        if (node.parentId) {
          filteredCallIdsSet.add(node.parentId);
          if (!foundCallIds.has(node.parentId)) {
            // Check if the parent id exists before adding to the process queue
            if (props.traceTreeFlat[node.parentId]) {
              itemsToProcess.push(node.parentId);
            }
          }
        }

        // Add children of direct matches in loose search mode
        // Don't add children of parent nodes that were added just to show the path
        if (foundCallIds.has(id)) {
          // Add all children recursively
          const addChildren = (nodeId: string) => {
            const currentNode = props.traceTreeFlat[nodeId];
            if (currentNode && currentNode.childrenIds) {
              for (const childId of currentNode.childrenIds) {
                if (!filteredCallIdsSet.has(childId)) {
                  filteredCallIdsSet.add(childId);
                  addChildren(childId);
                }
              }
            }
          };

          addChildren(id);
        }
      }
    }

    return [
      directMatches,
      Array.from(filteredCallIdsSet),
      Array.from(
        new Set([...filteredCallIdsSet].filter(x => !foundCallIds.has(x)))
      ),
    ];
  }, [props.traceTreeFlat, searchQuery, strictSearch]);

  const scrubbers: ScrubberOption[] = useMemo(() => {
    if (searchQuery === '') {
      return ['timeline', 'peer', 'sibling', 'stack'];
    }
    return ['timeline'];
  }, [searchQuery]);

  // Derived data
  const traceTreeFlat = useMemo(() => {
    if (searchQuery === '') {
      return props.traceTreeFlat;
    }
    const matched = new Set<string>(matchedCallIds);
    return Object.fromEntries(
      Object.entries(props.traceTreeFlat).filter(([_, node]) => {
        return matched.has(node.id);
      })
    );
  }, [matchedCallIds, props.traceTreeFlat, searchQuery]);

  // Navigation handler for Alt/Opt + Up/Down to navigate through tree calls
  const handleNavigateTree = useCallback(
    (direction: 1 | -1) => {
      // Get all nodes in DFS order (same as Timeline scrubber)
      const allNodes = Object.values(propsTraceTreeFlat)
        .sort((a, b) => a.dfsOrder - b.dfsOrder)
        .map(node => node.id);

      // In strict search mode, only navigate through matched calls
      const nodes =
        strictSearch && searchQuery !== '' && matchedCallIds
          ? matchedCallIds
              .filter(id => allNodes.includes(id))
              .sort((a, b) => {
                const aNode = propsTraceTreeFlat[a];
                const bNode = propsTraceTreeFlat[b];
                return aNode.dfsOrder - bNode.dfsOrder;
              })
          : allNodes;

      if (nodes.length === 0) {
        return;
      }

      // If no call is focused, focus the first node
      if (!focusedCallId) {
        setFocusedCallId(nodes[0]);
        return;
      }

      const currentIndex = nodes.indexOf(focusedCallId);
      // If current focused call is not in the navigation list, find nearest node
      if (currentIndex === -1) {
        setFocusedCallId(nodes[0]);
        return;
      }

      const newIndex = currentIndex + direction;
      if (newIndex >= 0 && newIndex < nodes.length) {
        setFocusedCallId(nodes[newIndex]);
      }
    },
    [
      propsTraceTreeFlat,
      focusedCallId,
      setFocusedCallId,
      strictSearch,
      searchQuery,
      matchedCallIds,
    ]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown' && event.altKey) {
        event.preventDefault();
        handleNavigateTree(1);
      } else if (event.key === 'ArrowUp' && event.altKey) {
        event.preventDefault();
        handleNavigateTree(-1);
      }
    },
    [handleNavigateTree]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <TreeViewHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        strictSearch={strictSearch}
        onToggleStrictSearch={() => setStrictSearch(!strictSearch)}
        metricVisibility={metricVisibility}
        onMetricVisibilityChange={handleMetricVisibilityChange}
      />
      <div className="flex-1 overflow-hidden">
        <TreeView
          {...props}
          filterCallIds={filteredCallIds}
          deemphasizeCallIds={deemphasizeCallIds}
          searchQuery={searchQuery}
          strictSearch={strictSearch}
          onSearchChange={setSearchQuery}
          onToggleStrictSearch={() => setStrictSearch(!strictSearch)}
          matchedCallIds={matchedCallIds}
          metricVisibility={metricVisibility}
        />
      </div>
      <TraceScrubber
        {...props}
        allowedScrubbers={scrubbers}
        traceTreeFlat={traceTreeFlat}
      />
    </div>
  );
};

export const TreeView: React.FC<
  TraceViewProps & {
    filterCallIds?: string[];
    deemphasizeCallIds?: string[];
    searchQuery?: string;
    strictSearch?: boolean;
    onSearchChange?: (value: string) => void;
    onToggleStrictSearch?: () => void;
    matchedCallIds?: string[];
    metricVisibility?: MetricVisibilitySettings;
  }
> = ({
  traceTreeFlat,
  focusedCallId,
  setFocusedCallId,
  filterCallIds,
  deemphasizeCallIds,
  setRootCallId,
  searchQuery,
  strictSearch,
  onSearchChange,
  onToggleStrictSearch,
  matchedCallIds,
  metricVisibility,
}) => {
  // Initialize expandedNodes with all node IDs
  const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(
    () => new Set([])
  );

  const filterSet = useMemo(
    () => (filterCallIds ? new Set(filterCallIds) : undefined),
    [filterCallIds]
  );

  const listRef = useRef<List>(null);

  const rootNodes = useMemo(() => {
    if (!filterCallIds || filterCallIds.length === 0) {
      return Object.values(traceTreeFlat).filter(
        node => !node.parentId || !traceTreeFlat[node.parentId]
      );
    }

    const filterIdSet = new Set(filterCallIds);
    return Object.values(traceTreeFlat).filter(node => {
      if (!filterIdSet.has(node.id)) {
        return false;
      }
      return (
        !node.parentId ||
        !traceTreeFlat[node.parentId] ||
        !filterIdSet.has(node.parentId)
      );
    });
  }, [traceTreeFlat, filterCallIds]);

  const flattenedNodes = useMemo(() => {
    const result: FlattenedNode[] = [];

    // In strict search mode, show all matched nodes at level 0 (flat list)
    if (strictSearch && searchQuery && matchedCallIds) {
      // Use matchedCallIds directly to get all matching nodes
      const matchedNodes = matchedCallIds
        .map(id => traceTreeFlat[id])
        .filter(node => node !== undefined)
        .sort((a, b) => a.dfsOrder - b.dfsOrder);

      matchedNodes.forEach(node => {
        result.push({
          id: node.id,
          call: node.call,
          level: 0, // All nodes at root level for flat list
          isExpanded: false, // No expansion in strict mode
          isVisible: true,
          childrenIds: [], // No children shown in strict mode
          hasDescendantErrors: node.descendantHasErrors,
        });
      });
      return result;
    }

    // Normal tree mode with hierarchy
    const processNode = (node: TraceTreeFlat[string], level: number) => {
      const isExpanded = !collapsedNodes.has(node.id);
      const filteredChildren = node.childrenIds.filter(childId => {
        return !filterSet || filterSet.has(childId);
      });
      result.push({
        id: node.id,
        call: node.call,
        level,
        isExpanded,
        isVisible: true,
        childrenIds: filteredChildren,
        hasDescendantErrors: node.descendantHasErrors,
      });

      if (isExpanded) {
        filteredChildren.forEach((childId: string) => {
          const child = traceTreeFlat[childId];
          if (child && (!filterSet || filterSet.has(childId))) {
            processNode(child, level + 1);
          }
        });
      }
    };

    rootNodes.forEach(node => processNode(node, 0));
    return result;
  }, [
    rootNodes,
    traceTreeFlat,
    collapsedNodes,
    filterSet,
    strictSearch,
    searchQuery,
    matchedCallIds,
  ]);

  const handleToggleExpand = (id: string, expandAll?: boolean) => {
    const newExpandedNodes = new Set(collapsedNodes);
    if (newExpandedNodes.has(id)) {
      // When expanding
      if (expandAll) {
        // Shift+click: remove current node and all descendants from collapsed nodes
        const removeDescendants = (nodeId: string) => {
          newExpandedNodes.delete(nodeId);
          const node = traceTreeFlat[nodeId];
          if (node) {
            node.childrenIds.forEach(childId => {
              removeDescendants(childId);
            });
          }
        };
        removeDescendants(id);
      } else {
        // Normal click: only remove current node from collapsed nodes
        newExpandedNodes.delete(id);
      }
    } else {
      // When collapsing, we add this node and all its descendants to collapsed nodes
      const addDescendants = (nodeId: string) => {
        newExpandedNodes.add(nodeId);
        const node = traceTreeFlat[nodeId];
        if (node) {
          node.childrenIds.forEach(childId => {
            addDescendants(childId);
          });
        }
      };
      addDescendants(id);
    }
    setCollapsedNodes(newExpandedNodes);
  };

  const rowRenderer = ({index, style}: any) => {
    const node = flattenedNodes[index];
    return (
      <TreeNode
        key={node.id}
        node={node}
        style={style}
        focusedCallId={focusedCallId}
        setFocusedCallId={setFocusedCallId}
        setRootCallId={setRootCallId}
        onToggleExpand={handleToggleExpand}
        deemphasizeCallIds={deemphasizeCallIds}
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        strictSearch={strictSearch}
        onToggleStrictSearch={onToggleStrictSearch}
        metricVisibility={metricVisibility ?? DEFAULT_METRIC_VISIBILITY}
      />
    );
  };

  // Scroll when selectedCallId changes
  useEffect(() => {
    const scrollToSelectedRow = () => {
      if (!focusedCallId || !listRef.current) {
        return;
      }

      const selectedIndex = flattenedNodes.findIndex(
        (node: FlattenedNode) => node.id === focusedCallId
      );
      if (selectedIndex === -1) {
        return;
      }

      listRef.current.scrollToRow(selectedIndex);
    };

    const timer = setTimeout(scrollToSelectedRow, 0);
    return () => clearTimeout(timer);
  }, [focusedCallId, flattenedNodes]);

  return (
    <div className="h-full overflow-hidden">
      <AutoSizer>
        {({width, height}) => (
          <List
            ref={listRef}
            width={width}
            height={height}
            rowCount={flattenedNodes.length}
            rowHeight={32}
            rowRenderer={rowRenderer}
            overscanRowCount={10}
            scrollToAlignment="auto"
          />
        )}
      </AutoSizer>
    </div>
  );
};
